// import libs
import { select, pointer } from 'd3-selection';
import { scaleLinear, scaleTime, scaleOrdinal } from 'd3-scale';
import { axisLeft, axisBottom } from 'd3-axis';
import { timeFormat } from 'd3-time-format';
import { bisector } from 'd3-array';
import {
	stack, line, area, stackOrderDescending, curveMonotoneX as curve,
} from 'd3-shape';

const tooltipTimeFormatter = timeFormat( '%B %d, %Y' );

const formatPercent = ( n ) => {
	n *= 100;

	if ( n > 0 && n < 1 ) { return '<1%'; }
	return `${n.toFixed( 0 )}%`;
};

const Chart = {
	data: null,
	paddedData: null,
	bounds: null,
	colors: null,
	colorScale: null,
	keys: null,
	chartOrder: null,
	labels: null,
	bisector: null,
	xScale: null,
	yScale: null,
	yAxisWidth: null,
	preliminaryWeeks: null,
	width: null,
	height: 350,
	margin: {
		top: 15,
		right: 5,
		bottom: 45,
		left: 5,
	},
	text: {
		label: '',
		axisLabel: '',
	},
	tooltip: null,
	chart: {
		width: null,
		height: null,
		svg: null,
		defs: null,
		elem: null,
		areas: null,
		annotations: null,
		labels: null,
		axisLabels: null,
	},

	init( settings ) {
		// Initialize container
		this.$chart = document.querySelector( '.l-interactive__chart' );
		this.$tip = document.querySelector( '.l-interactive__tooltip' );

		// Initialize settings
		this.data = settings.data;
		this.bounds = settings.bounds;
		this.colors = settings.colors;
		this.text.label = settings.label;
		this.text.axisLabel = settings.axisLabel;
		this.keys = settings.keys;
		this.chartOrder = settings.chartOrder;
		this.labels = settings.labels;
		this.preliminaryWeeks = settings.preliminaryWeeks;

		// Add dummy data so that the last week is fully rendered
		this.paddedData = [
			...this.data,
			{ ...this.data[this.data.length - 1], date: this.bounds.maxX },
		];

		// Time formatter
		this.chart.timeFormatter = timeFormat( '%b %d' );

		// Bisector
		this.bisector = bisector( ( item ) => item.date ).left;

		const altText = this.generateAltText( this.data[this.data.length - 1]);

		// Initialize svg
		this.chart.svg = select( '.l-interactive__chart' )
			.attr( 'width', '100%' )
			.attr( 'height', this.height )
			.attr( 'preserveAspectRatio', 'xMidYMin meet' )
			.attr( 'role', 'img' )
			.attr( 'aria-label', altText );

		// Hatch fill
		this.chart.defs = this.chart.svg.append( 'defs' )
			.html( '<pattern id="diagonal-hatch" patternUnits="userSpaceOnUse" width="10" height="10"><image xlink:href="data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxMCcgaGVpZ2h0PScxMCc+CiAgPHJlY3Qgd2lkdGg9JzEwJyBoZWlnaHQ9JzEwJyBmaWxsPSd3aGl0ZScvPgogIDxwYXRoIGQ9J00tMSwxIGwyLC0yCiAgICAgICAgICAgTTAsMTAgbDEwLC0xMAogICAgICAgICAgIE05LDExIGwyLC0yJyBzdHJva2U9J2JsYWNrJyBzdHJva2Utd2lkdGg9JzMnLz4KPC9zdmc+" x="0" y="0" width="10" height="10"></image></pattern>' );

		// Axis labels
		this.chart.axisLabels = this.chart.svg.append( 'g' );

		// Initialize chart
		this.chart.elem = this.chart.svg.append( 'g' )
			.attr( 'class', 'c-chart__inner' );

		// Initialize tooltip
		this.tooltip = select( '.l-interactive__tooltip' );

		this.yScale = scaleLinear();

		this.colorScale = scaleOrdinal(
			this.keys, this.colors,
		)
			.unknown( '#bababa' );

		// Initialize Y axis
		this.chart.elem.append( 'g' )
			.attr( 'class', 'c-chart__axis c-chart__axis--shadow' );

		// Initialize Y axis
		this.chart.elem.append( 'g' )
			.attr( 'class', 'c-chart__axis c-chart__axis--y' );

		// Initialize X axis
		this.xScale = scaleTime();

		// Initialize X axis items
		this.chart.elem.append( 'g' )
			.attr( 'class', 'c-chart__axis c-chart__axis--x' );

		// Initialize Y axis grid
		this.chart.elem.append( 'g' )
			.attr( 'class', 'c-chart__grid' );

		// Render chart
		this.render();
	},

	drawAnnotations() {
		this.chart.annotations = this.chart.elem.append( 'g' )
			.attr( 'class', 'c-chart__annotations c-chart__content' );

		const provisionalStartDate = this.data[this.data.length - this.preliminaryWeeks].date;
		const xStart = this.xScale( provisionalStartDate );
		const xEnd = this.xScale( this.bounds.maxX );
		const xWidth = xEnd - xStart;

		const provisional = this.chart.annotations.append( 'g' )
			.attr( 'transform', `translate(${xStart}, 0)` );

		// Draw shaded rectangle over last three weeks of data
		provisional
			.append( 'rect' )
			.attr( 'x', 0 )
			.attr( 'y', this.margin.top )
			.attr( 'width', xWidth )
			.attr( 'height', this.chart.height - this.margin.top )
			.attr( 'opacity', 0.2 )
			.attr( 'fill', 'url(#diagonal-hatch)' );

		// Label rectangle
		provisional.append( 'text' )
			.attr( 'text-anchor', 'end' )
			.attr( 'dx', xWidth - 1 )
			.attr( 'dy', '0.8em' )
			.attr( 'fill', 'currentColor' )
			.text( 'Preliminary data' );
	},

	drawAreas() {
		this.chart.areas = this.chart.elem.append( 'g' )
			.attr( 'class', 'c-chart__lines c-chart__content' );

		const chartStack = stack()
			.keys( this.chartOrder );

		const series = chartStack( this.paddedData );

		const chartArea = area()
			.x( ({ data }) => this.xScale( data.date ) )
			.y0( ([y0]) => this.yScale( y0 ) )
			.y1( ([, y1]) => this.yScale( y1 ) )
			.curve( curve );

		const chartLine = line()
			.x( ({ data }) => this.xScale( data.date ) )
			.y( ([, y]) => this.yScale( y ) )
			.curve( curve );

		const areas = this.chart.areas
			.selectAll( 'g' )
			.data( series )
			.join( 'g' );

		areas
			.append( 'path' )
			.attr( 'd', chartArea )
			.attr( 'fill', ({ key }) => this.colorScale( key ) )
			.attr( 'class', 'c-chart__area' );

		areas
			.append( 'path' )
			.attr( 'd', chartLine )
			.attr( 'fill', 'none' )
			.attr( 'stroke-width', 1.5 )
			.attr( 'stroke', ({ key }) => this.colorScale( key ) )
			.attr( 'class', 'c-chart__line' );

		areas.filter( ( d ) => 'delta' === d.key ).raise();
	},

	drawTooltip() {
		// Tooltip logic
		const focus = this.chart.elem.append( 'g' )
			.attr( 'class', 'c-chart__focus c-chart__content' )
			.style( 'display', 'none' );

		const focusWidth = this.xScale( new Date( '2021-10-10' ) ) - this.xScale( new Date( '2021-10-03' ) );

		focus.append( 'rect' )
			.attr( 'fill', '#000' )
			.attr( 'opacity', 0.15 )
			.attr( 'x', 0 )
			.attr( 'y', this.margin.top )
			.attr( 'width', focusWidth )
			.attr( 'height', this.chart.height - this.margin.top );

		this.chart.elem.append( 'rect' )
			.attr( 'class', 'c-chart__overlay c-chart__content' )
			.attr( 'width', this.chart.width )
			.attr( 'height', this.chart.height )
			.attr( 'fill', '#fff' )
			.on( 'mousemove', ( event ) => {
				// intersection logic
				const [mouseX, mouseY] = pointer( event );
				const xVal = this.xScale.invert( mouseX );
				const i = this.bisector( this.data, xVal, 1 );
				const itemPrev = this.data[i - 1];
				const itemNext = i < this.data.length - 1 ? this.data[i] : itemPrev;
				const itemSelected = xVal - itemPrev.date > itemNext.date - xVal ? itemNext : itemPrev;

				// tooltip variables
				const {
					left: chartLeftOffset,
					top: chartTopOffset,
					right: chartRightOffset,
				} = this.$chart.getBoundingClientRect();
				const itemLeftOffset = this.xScale( itemSelected.date );
				const leftOffset = chartLeftOffset + itemLeftOffset;
				const topOffset = mouseY;

				focus.style( 'display', 'inline-block' )
					.attr( 'transform', `translate(${this.xScale( itemSelected.date )}, 0)` );

				const tooltipLegend = this.keys
					.map( ( key ) => `<li style="--seriesColor: ${this.colorScale( key )}">${this.labels.get( key )} <strong>${formatPercent( itemSelected[key])}</strong></li>` )
					.join( '' );

				this.tooltip
					.style( 'display', 'inline-block' )
					.style( 'visibility', 'hidden' )
					.html(
						`<div>Week of ${tooltipTimeFormatter( itemSelected.date )}</div>
						<ul class="l-interactive__legend">${tooltipLegend}</ul>`,
					);

				const tipWidth = this.$tip.offsetWidth;
				const tipTopOffset = Math.max( topOffset, chartTopOffset );
				let tipLeftOffset = leftOffset - tipWidth;

				if ( tipLeftOffset < chartLeftOffset ) {
					const newOffset = chartLeftOffset + itemLeftOffset + focusWidth + this.margin.left * 2;
					tipLeftOffset = Math.min( newOffset, chartRightOffset - tipWidth );
				}

				this.tooltip
					.style( 'left', `${tipLeftOffset}px` )
					.style( 'top', `${tipTopOffset}px` )
					.style( 'display', 'inline-block' )
					.style( 'visibility', 'visible' );
			})
			.on( 'mouseout', () => {
				focus.style( 'display', 'none' );
				this.tooltip.style( 'display', 'none' );
			});
	},

	render() {
		// Set dimensions
		this.width = this.$chart.clientWidth;
		this.chart.width = this.width - ( this.margin.left + this.margin.right );
		this.chart.height = this.height - ( this.margin.top + this.margin.bottom );

		// SVG
		this.chart.svg.attr( 'viewBox', `0 0 ${this.width} ${this.height}` );

		// Chart
		this.chart.svg.select( '.c-chart__inner' )
			.attr( 'transform', `translate(${this.margin.left}, ${this.margin.top})` );

		// Remove exiting chart content
		this.chart.svg.selectAll( '.c-chart__content' )
			.remove();

		// Specify Y Axis Range
		this.yScale.range([this.chart.height, this.margin.top])
			.domain([this.bounds.minY, this.bounds.maxY]);

		// Draw Y Axis
		this.chart.elem.select( '.c-chart__axis--y' )
			.call( axisLeft( this.yScale )
				.ticks( 4 )
				.tickSize( 0 )
				.tickPadding( 12 )
				.tickFormat( ( tick ) => `${tick * 100}%` ) )
			.attr( 'text-anchor', 'start' )
			.attr( 'transform', () => {
				this.yAxisWidth = document.querySelector( '.c-chart__axis--y' ).getBBox().width;
				return 'translate(12, -12)';
			});

		// Specify X Axis Range
		this.xScale.range([0, this.chart.width])
			.domain([this.bounds.minX, this.bounds.maxX]);

		// Draw X Axis
		this.chart.elem.select( '.c-chart__axis--x' )
			.attr( 'transform', `translate(0, ${this.chart.height})` )
			.call( axisBottom( this.xScale )
				.tickValues( this.calculateXTicks( 5 ) )
				.tickSize( 0 )
				.tickPadding( 12 )
				.tickFormat( this.chart.timeFormatter ) );

		// Draw X Axis Label
		this.chart.axisLabels.append( 'text' )
			.attr( 'class', 'c-chart__axisLabel c-chart__content' )
			.attr( 'fill', 'currentColor' )
			.attr( 'transform', `translate(${this.margin.left}, ${this.height - 3})` )
			.text( this.text.axisLabel );

		// Draw Y Axis Grid
		this.chart.elem.select( '.c-chart__grid' )
			.call( axisLeft( this.yScale )
				.ticks( 4 )
				.tickFormat( '' )
				.tickSize( - this.chart.width ) )
			.selectAll( 'line' )
			.style( 'stroke', '#EDEDF0' );

		// Draw annotations
		this.drawAnnotations();

		// Draw areas
		this.drawAreas();

		// Draw tooltip
		this.drawTooltip();
	},

	calculateXTicks( maxTicks ) {
		const tickData = [...this.data];
		const tickInterval = Math.round( tickData.length / maxTicks );
		const tickItems = tickData.reverse().filter( ( item, i ) => 0 === ( i - 3 ) % tickInterval );
		return tickItems.reverse().map( ( item ) => new Date( item.date ) );
	},

	generateAltText( data ) {
		const dataToInclude = Object.entries( data )
			.filter( ([key, value]) => ! ['date', 'n_samples', 'other'].includes( key ) && value > 0 )
			.sort( ( a, b ) => b[1] - a[1])
			.map( ([key, value]) => `${formatPercent( value )} ${this.labels.get( key )}` );

		const dateFormatted = tooltipTimeFormatter( data.date );

		let dataFormatted;

		if ( ! dataToInclude.length ) { return ''; }
		if ( 1 === dataToInclude.length ) {
			dataFormatted = dataToInclude[0];
		} else if ( 2 === dataToInclude.length ) {
			dataFormatted = dataToInclude.join( ' and ' );
		} else {
			const firstEntries = dataToInclude.slice( 0, - 1 );
			const lastEntry = dataToInclude[dataToInclude.length - 1];
			dataFormatted = `${firstEntries.join( ', ' )}, and ${lastEntry}`;
		}

		return `An area chart showing the share of different COVID-19 variants over time. For the week of ${dateFormatted}, the breakdown is ${dataFormatted}.`;
	},
};

export default Chart;
